import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { API_URL } from '../lib/api'
import { useUTM } from '../hooks/useUTM'

interface BlogPost {
  id: string
  slug: string
  title: string
  meta_description: string
  published_at: string
  reading_time_minutes: number
}

export default function Blog() {
  useUTM()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${API_URL}/api/blog`)
      .then(res => res.json())
      .then(data => {
        setPosts(data.data || data || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <>
      <section className="page-hero page-hero--short">
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Insights</span>
            <h1>Blog</h1>
            <p>Insights on carnivore, fasting, and thriving</p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            {loading ? (
              <p style={{ textAlign: 'center', padding: '3rem 0', color: '#666' }}>Loading...</p>
            ) : posts.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '3rem 0', color: '#666' }}>
                No posts yet. Check back soon.
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '2rem',
              }}>
                {posts.map((post, i) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      style={{
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '5px',
                        padding: '2rem',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                        (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                      }}
                    >
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        color: '#0f172a',
                        marginBottom: '0.75rem',
                        lineHeight: 1.4,
                      }}>
                        {post.title}
                      </h3>
                      <p style={{
                        fontSize: '0.95rem',
                        color: '#555',
                        lineHeight: 1.6,
                        marginBottom: '1rem',
                      }}>
                        {post.meta_description}
                      </p>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.825rem',
                        color: '#999',
                      }}>
                        <span>{formatDate(post.published_at)}</span>
                        <span>{post.reading_time_minutes || 5} min read</span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  )
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}
